import React, { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "";

const emptyCategory = {
  name: "",
};

const emptyItem = {
  categoryId: "",
  name: "",
  tag: "",
  price: "",
};

const AddonsCms = () => {
  const [groups, setGroups] = useState([]);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  });

  const selectedGroup = useMemo(
    () =>
      groups.find(
        (group) => String(group._id) === String(itemForm.categoryId)
      ),
    [groups, itemForm.categoryId]
  );

  const loadAll = async () => {
    const response = await fetch(`${API_BASE_URL}/api/addons`);
    const result = response.ok ? await response.json() : [];
    setGroups(Array.isArray(result) ? result : []);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await loadAll();
      } catch (error) {
        console.error(error);
        alert("Failed to load addons");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const resetCategoryForm = () => {
    setEditingCategoryId(null);
    setCategoryForm(emptyCategory);
  };

  const resetItemForm = () => {
    setEditingItemId(null);
    setItemForm((previous) => ({
      ...emptyItem,
      categoryId: previous.categoryId,
    }));
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      alert("Please enter an event name, for example Meal Combo");
      return;
    }

    try {
      setSaving(true);

      const url = editingCategoryId
        ? `${API_BASE_URL}/api/addons/categories/${editingCategoryId}`
        : `${API_BASE_URL}/api/addons/categories`;

      const response = await fetch(url, {
        method: editingCategoryId ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify({ name: categoryForm.name.trim() }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to save event");
      }

      await loadAll();
      resetCategoryForm();
      alert(result.message || "Saved");
    } catch (error) {
      alert(error.message || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (group) => {
    if (
      !window.confirm(
        `Delete "${group.name}" and all items under it?`
      )
    ) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(
        `${API_BASE_URL}/api/addons/categories/${group._id}`,
        {
          method: "DELETE",
          headers: authHeaders(),
        }
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete event");
      }

      await loadAll();
      if (editingCategoryId === group._id) {
        resetCategoryForm();
      }
      if (String(itemForm.categoryId) === String(group._id)) {
        setItemForm(emptyItem);
        setEditingItemId(null);
      }
    } catch (error) {
      alert(error.message || "Failed to delete event");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveItem = async () => {
    if (!itemForm.categoryId) {
      alert("Please select an event first");
      return;
    }

    if (!itemForm.name.trim()) {
      alert("Please enter an item name");
      return;
    }

    if (itemForm.price === "" || Number(itemForm.price) < 0) {
      alert("Please enter a valid price");
      return;
    }

    try {
      setSaving(true);

      const url = editingItemId
        ? `${API_BASE_URL}/api/addons/items/${editingItemId}`
        : `${API_BASE_URL}/api/addons/items`;

      const response = await fetch(url, {
        method: editingItemId ? "PUT" : "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          categoryId: itemForm.categoryId,
          name: itemForm.name.trim(),
          tag: itemForm.tag.trim(),
          price: Number(itemForm.price),
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to save item");
      }

      await loadAll();
      resetItemForm();
      alert(result.message || "Saved");
    } catch (error) {
      alert(error.message || "Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(
        `${API_BASE_URL}/api/addons/items/${item._id}`,
        {
          method: "DELETE",
          headers: authHeaders(),
        }
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to delete item");
      }

      await loadAll();
      if (editingItemId === item._id) {
        resetItemForm();
      }
    } catch (error) {
      alert(error.message || "Failed to delete item");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Addons</h1>
          <p>
            Create events first (Meal Combo, Starter, Main Course, Decoration).
            Then add items with a name and price per quantity.
          </p>
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{editingCategoryId ? "Update Event" : "Add Event"}</h2>
          <p>
            These names become the filter tabs on the add-ons page.
          </p>
        </div>

        <div className="cms-form-grid cms-form-grid-4">
          <div className="cms-form-group">
            <label>Event name</label>
            <input
              type="text"
              value={categoryForm.name}
              onChange={(e) =>
                setCategoryForm({ name: e.target.value })
              }
              placeholder="Meal Combo"
            />
          </div>
        </div>

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSaveCategory}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : editingCategoryId
                ? "Save Changes"
                : "Save Event"}
          </button>
          {editingCategoryId ? (
            <button
              type="button"
              className="cms-upload-btn"
              onClick={resetCategoryForm}
            >
              Cancel Update
            </button>
          ) : null}
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>Events</h2>
        </div>

        {groups.length === 0 ? (
          <p className="cms-inquiries-empty">No events yet.</p>
        ) : (
          <div className="cms-enquiries-table-wrap">
            <table className="cms-enquiries-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Event</th>
                  <th>Items</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group, index) => (
                  <tr key={group._id}>
                    <td>{index + 1}</td>
                    <td>{group.name}</td>
                    <td>{group.items?.length || 0}</td>
                    <td>
                      <div className="cms-item-row-actions">
                        <button
                          type="button"
                          className="cms-upload-btn"
                          onClick={() => {
                            setEditingCategoryId(group._id);
                            setCategoryForm({ name: group.name });
                            setItemForm((previous) => ({
                              ...previous,
                              categoryId: String(group._id),
                            }));
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="cms-delete-btn"
                          onClick={() => handleDeleteCategory(group)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{editingItemId ? "Update Item" : "Add Item"}</h2>
          <p>
            Price is charged per quantity. Guests can choose up to 10 on the
            website.
          </p>
        </div>

        <div className="cms-form-grid cms-form-grid-4">
          <div className="cms-form-group">
            <label>Event</label>
            <select
              value={itemForm.categoryId}
              onChange={(e) =>
                setItemForm((previous) => ({
                  ...previous,
                  categoryId: e.target.value,
                }))
              }
            >
              <option value="">Select event</option>
              {groups.map((group) => (
                <option key={group._id} value={String(group._id)}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
          <div className="cms-form-group">
            <label>Item name</label>
            <input
              type="text"
              value={itemForm.name}
              onChange={(e) =>
                setItemForm((previous) => ({
                  ...previous,
                  name: e.target.value,
                }))
              }
              placeholder="Veg Meal Combo"
            />
          </div>
          <div className="cms-form-group">
            <label>Tag (optional)</label>
            <input
              type="text"
              value={itemForm.tag}
              onChange={(e) =>
                setItemForm((previous) => ({
                  ...previous,
                  tag: e.target.value,
                }))
              }
              placeholder="VEG, NON-VEG"
            />
          </div>
          <div className="cms-form-group">
            <label>Price per quantity (₹)</label>
            <input
              type="number"
              min="0"
              value={itemForm.price}
              onChange={(e) =>
                setItemForm((previous) => ({
                  ...previous,
                  price: e.target.value === "" ? "" : Math.max(0, Number(e.target.value)),
                }))
              }
            />
          </div>
        </div>

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSaveItem}
            disabled={saving || groups.length === 0}
          >
            {saving
              ? "Saving..."
              : editingItemId
                ? "Save Changes"
                : "Save Item"}
          </button>
          {editingItemId ? (
            <button
              type="button"
              className="cms-upload-btn"
              onClick={resetItemForm}
            >
              Cancel Update
            </button>
          ) : null}
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>
            {selectedGroup
              ? `Items in ${selectedGroup.name}`
              : "Items"}
          </h2>
        </div>

        {!selectedGroup ? (
          <p className="cms-inquiries-empty">
            Select an event above to see and add its items.
          </p>
        ) : (selectedGroup.items || []).length === 0 ? (
          <p className="cms-inquiries-empty">No items in this event yet.</p>
        ) : (
          <div className="cms-enquiries-table-wrap">
            <table className="cms-enquiries-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Tag</th>
                  <th>Price</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(selectedGroup.items || []).map((item, index) => (
                  <tr key={item._id}>
                    <td>{index + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.tag || "—"}</td>
                    <td>
                      ₹ {Number(item.price).toLocaleString("en-IN")}
                    </td>
                    <td>
                      <div className="cms-item-row-actions">
                        <button
                          type="button"
                          className="cms-upload-btn"
                          onClick={() => {
                            setEditingItemId(item._id);
                            setItemForm({
                              categoryId: String(selectedGroup._id),
                              name: item.name,
                              tag: item.tag || "",
                              price: item.price,
                            });
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="cms-delete-btn"
                          onClick={() => handleDeleteItem(item)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddonsCms;
